import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement[]} media
 * @property {HTMLElement} thumbnails
 * @property {HTMLElement} [currentIndex]
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
  }

  /**
   * Abre o modal na imagem selecionada.
   *
   * @param {number} index
   * @param {PointerEvent} event
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const openDialog = () => {
      dialog.showModal();

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({
          behavior: 'instant',
          block: 'center',
          inline: 'center',
        });
      }
    };

    const sourceImage =
      event.target instanceof Element
        ? event.target.closest('li, slideshow-slide')
        : null;

    if (!supportsViewTransitions || !sourceImage || !targetImage) {
      openDialog();
      this.selectThumbnail(index, { behavior: 'instant' });
      return;
    }

    const transitionName = 'gallery-item';

    sourceImage.style.setProperty(
      'view-transition-name',
      transitionName
    );

    await startViewTransition(() => {
      openDialog();

      sourceImage.style.removeProperty(
        'view-transition-name'
      );

      targetImage.style.setProperty(
        'view-transition-name',
        transitionName
      );
    });

    targetImage.style.removeProperty(
      'view-transition-name'
    );

    this.selectThumbnail(index, {
      behavior: 'instant',
    });
  }

  /**
   * Carrega a imagem de alta resolução.
   *
   * @param {HTMLElement} mediaContainer
   */
  #loadHighResolutionImage(mediaContainer) {
    if (
      !mediaContainer.classList.contains(
        'product-media-container--image'
      )
    ) {
      return false;
    }

    const image = mediaContainer.querySelector(
      'img.product-media__image'
    );

    if (
      !image ||
      !(image instanceof HTMLImageElement)
    ) {
      return false;
    }

    const highResolutionUrl =
      image.getAttribute('data_max_resolution');

    if (
      !highResolutionUrl ||
      this.#highResImagesLoaded.has(
        highResolutionUrl
      )
    ) {
      return false;
    }

    preloadImage(highResolutionUrl);

    const newImage = new Image();

    newImage.className = image.className;
    newImage.alt = image.alt;

    newImage.setAttribute(
      'data_max_resolution',
      highResolutionUrl
    );

    newImage.onload = () => {
      image.replaceWith(newImage);

      this.#highResImagesLoaded.add(
        highResolutionUrl
      );
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Atualiza miniatura e contador durante a navegação.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisibleElement =
      await getMostVisibleElement(media);

    const activeIndex = media.indexOf(
      mostVisibleElement
    );

    const targetThumbnail =
      thumbnails.children[activeIndex];

    if (
      !targetThumbnail ||
      !(targetThumbnail instanceof HTMLElement)
    ) {
      return;
    }

    Array.from(
      thumbnails.querySelectorAll('button')
    ).forEach((button, index) => {
      button.setAttribute(
        'aria-selected',
        `${index === activeIndex}`
      );
    });

    this.#updateCounter(activeIndex);
    this.#loadHighResolutionImage(
      mostVisibleElement
    );

    this.dispatchEvent(
      new ZoomMediaSelectedEvent(activeIndex)
    );
  }, 50);

  /**
   * Fecha o modal.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions) {
      this.closeDialog();
      return;
    }

    const mostVisibleElement =
      await getMostVisibleElement(media);

    const activeIndex = media.indexOf(
      mostVisibleElement
    );

    const transitionName = 'gallery-item';

    const mediaGallery =
      /** @type {import('./media-gallery').MediaGallery | undefined} */ (
        this.closest('media-gallery')
      );

    const slideshowActive =
      mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive
      ? mediaGallery.slideshow?.slides?.[
          activeIndex
        ]
      : mediaGallery?.media?.[activeIndex];

    if (!slide) {
      this.closeDialog();
      return;
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(
      this.refs.thumbnails
    );

    mostVisibleElement.style.setProperty(
      'view-transition-name',
      transitionName
    );

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty(
        'view-transition-name'
      );

      slide.style.setProperty(
        'view-transition-name',
        transitionName
      );

      this.closeDialog();
    });

    slide.style.removeProperty(
      'view-transition-name'
    );

    dialog.classList.remove(
      'dialog--closed'
    );
  }

  closeDialog() {
    const { dialog } = this.refs;

    if (dialog.open) {
      dialog.close();
    }

    window.dispatchEvent(
      new DialogCloseEvent()
    );
  }

  /**
   * Navegação pelo teclado.
   *
   * @param {KeyboardEvent} event
   */
  handleKeyDown(event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.previousMedia();
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.nextMedia();
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  }

  /**
   * Exibe a imagem anterior.
   */
  previousMedia() {
    const total = this.refs.media.length;

    if (total < 2) return;

    const previousIndex =
      (this.#getActiveIndex() - 1 + total) %
      total;

    const behavior = prefersReducedMotion()
      ? 'instant'
      : 'smooth';

    this.selectThumbnail(previousIndex, {
      behavior,
    });
  }

  /**
   * Exibe a próxima imagem.
   */
  nextMedia() {
    const total = this.refs.media.length;

    if (total < 2) return;

    const nextIndex =
      (this.#getActiveIndex() + 1) %
      total;

    const behavior = prefersReducedMotion()
      ? 'instant'
      : 'smooth';

    this.selectThumbnail(nextIndex, {
      behavior,
    });
  }

  /**
   * Retorna o índice ativo.
   *
   * @returns {number}
   */
  #getActiveIndex() {
    const thumbnails = Array.from(
      this.refs.thumbnails.children
    );

    const selectedIndex =
      thumbnails.findIndex(
        (thumbnail) =>
          thumbnail.getAttribute(
            'aria-selected'
          ) === 'true'
      );

    return selectedIndex >= 0
      ? selectedIndex
      : 0;
  }

  /**
   * Atualiza o contador.
   *
   * @param {number} index
   */
  #updateCounter(index) {
    if (this.refs.currentIndex) {
      this.refs.currentIndex.textContent =
        `${index + 1}`;
    }
  }

  /**
   * Clique em uma miniatura.
   *
   * @param {number} index
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion()
      ? 'instant'
      : 'smooth';

    this.selectThumbnail(index, {
      behavior,
    });
  }

  /**
   * Pré-carrega a imagem ao passar o mouse.
   *
   * @param {number} index
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;

    if (!media[index]) return;

    this.#loadHighResolutionImage(
      media[index]
    );
  }

  /**
   * Seleciona a imagem e sua miniatura.
   *
   * @param {number} index
   * @param {{ behavior: ScrollBehavior }} options
   */
  async selectThumbnail(
    index,
    options = { behavior: 'smooth' }
  ) {
    const { media, thumbnails } = this.refs;

    if (
      !thumbnails ||
      !thumbnails.children.length
    ) {
      return;
    }

    if (
      Number.isNaN(index) ||
      index < 0 ||
      index >= thumbnails.children.length
    ) {
      return;
    }

    const targetThumbnail =
      thumbnails.children[index];

    if (
      !targetThumbnail ||
      !(targetThumbnail instanceof HTMLElement)
    ) {
      return;
    }

    Array.from(
      thumbnails.querySelectorAll('button')
    ).forEach((button, buttonIndex) => {
      button.setAttribute(
        'aria-selected',
        `${buttonIndex === index}`
      );
    });

    this.#updateCounter(index);

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
        block: 'center',
        inline: 'center',
      });

      this.#loadHighResolutionImage(
        targetImage
      );
    }

    this.dispatchEvent(
      new ZoomMediaSelectedEvent(index)
    );
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define(
    'zoom-dialog',
    ZoomDialog
  );
}

/**
 * Localiza o elemento mais visível.
 *
 * @param {HTMLElement[]} elements
 * @returns {Promise<HTMLElement>}
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer =
      new IntersectionObserver(
        (entries) => {
          const mostVisible =
            entries.reduce(
              (previous, current) =>
                current.intersectionRatio >
                previous.intersectionRatio
                  ? current
                  : previous
            );

          observer.disconnect();

          resolve(
            /** @type {HTMLElement} */ (
              mostVisible.target
            )
          );
        },
        {
          threshold: Array.from(
            { length: 100 },
            (_, index) => index / 100
          ),
        }
      );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}